<template>
  <div class="threat-node-details" :class="{ 'light-theme': !isDark }">
    <div class="details-header">
      <div class="node-type-badge" :class="node.type">{{ nodeTypeLabel }}</div>
      <div class="header-actions">
        <!-- Reasoning flip button - only for APT groups with reasoning data -->
        <v-btn
          v-if="hasReasoning"
          icon
          x-small
          class="reasoning-toggle-btn"
          :class="{ 'active': showReasoning }"
          @click="toggleReasoning"
        >
          <v-icon small :color="showReasoning ? '#00f5ff' : (isDark ? 'white' : '#666')">
            {{ showReasoning ? 'mdi-card-account-details' : 'mdi-lightbulb-on-outline' }}
          </v-icon>
        </v-btn>
        <v-btn icon x-small @click="$emit('close')">
          <v-icon small :color="isDark ? 'white' : '#666'">mdi-close</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Flip Card Container -->
    <div class="flip-card-container" :class="{ 'flipped': showReasoning }">
      <!-- Front Side - Normal Details -->
      <div class="flip-card-front">
        <div class="details-content">
      <h3 class="node-title">{{ node.label }}</h3>

      <!-- Organization Details -->
      <template v-if="node.type === 'org'">
        <div class="detail-section">
          <div class="section-title">Overview</div>
          <p class="section-text">Central organization node showing threat landscape connections.</p>
        </div>
        <div class="stats-row">
          <div class="stat-item">
            <span class="stat-value">{{ connectedCves.length }}</span>
            <span class="stat-label">CVEs</span>
          </div>
          <div class="stat-item">
            <span class="stat-value">{{ connectedTtps.length }}</span>
            <span class="stat-label">TTPs</span>
          </div>
          <div class="stat-item">
            <span class="stat-value">{{ connectedApts.length }}</span>
            <span class="stat-label">APT Groups</span>
          </div>
        </div>
      </template>

      <!-- CVE Details -->
      <template v-else-if="node.type === 'cve'">
        <div v-if="node.data?.cvss" class="cvss-badge" :class="cvssClass">
          CVSS {{ node.data.cvss }}
        </div>

        <!-- Reasoning Section -->
        <div v-if="node.data?.attackVector" class="detail-section">
          <div class="section-header">
            <v-icon x-small color="#00f5ff" class="section-icon">mdi-lightbulb-outline</v-icon>
            <span class="section-title">Reasoning</span>
          </div>
          <v-tooltip bottom max-width="400" content-class="detail-tooltip">
            <template v-slot:activator="{ on, attrs }">
              <p class="section-text highlight-blue truncate-text" v-bind="attrs" v-on="on">{{ node.data.attackVector }}</p>
            </template>
            <span>{{ node.data.attackVector }}</span>
          </v-tooltip>
        </div>

        <!-- Potential Risk Section -->
        <div v-if="node.data?.potentialRisk" class="detail-section">
          <div class="section-header">
            <v-icon x-small color="#ffcc00" class="section-icon">mdi-alert-outline</v-icon>
            <span class="section-title">Potential Impact</span>
          </div>
          <v-tooltip bottom max-width="400" content-class="detail-tooltip">
            <template v-slot:activator="{ on, attrs }">
              <p class="section-text highlight-yellow truncate-text" v-bind="attrs" v-on="on">{{ node.data.potentialRisk }}</p>
            </template>
            <span>{{ node.data.potentialRisk }}</span>
          </v-tooltip>
        </div>

        <!-- Connected TTPs -->
        <div v-if="connectedTtps.length" class="detail-section">
          <div class="section-title">Related TTPs ({{ connectedTtps.length }})</div>
          <div class="chips-container">
            <span v-for="ttp in connectedTtps.slice(0, 8)" :key="ttp" class="detail-chip ttp">{{ ttp }}</span>
            <span v-if="connectedTtps.length > 8" class="more-count">+{{ connectedTtps.length - 8 }} more</span>
          </div>
        </div>

        <!-- Related APT Groups -->
        <div v-if="relatedAptGroups.length" class="detail-section">
          <div class="section-title">Threat Actors Using This CVE</div>
          <div class="apt-list">
            <div v-for="apt in relatedAptGroups.slice(0, 5)" :key="apt.name" class="apt-item-small">
              <span class="apt-color-dot" :style="{ backgroundColor: apt.color || '#ff3355' }" />
              <span class="apt-name-text">{{ apt.name }}</span>
            </div>
          </div>
        </div>
      </template>

      <!-- TTP Details -->
      <template v-else-if="node.type === 'ttp'">
        <div v-if="node.data?.tactics?.length" class="detail-section">
          <div class="section-title">MITRE ATT&CK Tactics</div>
          <div class="chips-container">
            <span v-for="tactic in node.data.tactics" :key="tactic" class="detail-chip tactic">{{ formatTactic(tactic) }}</span>
          </div>
        </div>

        <div v-if="node.data?.description" class="detail-section">
          <div class="section-title">Description</div>
          <p class="section-text">{{ truncateText(node.data.description, 250) }}</p>
        </div>

        <div v-if="node.data?.maturity_level" class="detail-section">
          <div class="section-title">Maturity Level</div>
          <div class="maturity-badge">{{ node.data.maturity_level }}</div>
        </div>

        <div v-if="connectedCves.length" class="detail-section">
          <div class="section-title">CVEs Using This Technique ({{ connectedCves.length }})</div>
          <div class="chips-container">
            <span v-for="cve in connectedCves.slice(0, 6)" :key="cve" class="detail-chip cve">{{ cve }}</span>
            <span v-if="connectedCves.length > 6" class="more-count">+{{ connectedCves.length - 6 }} more</span>
          </div>
        </div>

        <div v-if="connectedApts.length" class="detail-section">
          <div class="section-title">APT Groups Using This ({{ connectedApts.length }})</div>
          <div class="chips-container">
            <span v-for="apt in connectedApts.slice(0, 6)" :key="apt" class="detail-chip apt">{{ apt }}</span>
            <span v-if="connectedApts.length > 6" class="more-count">+{{ connectedApts.length - 6 }} more</span>
          </div>
        </div>
      </template>

      <!-- CWE Details -->
      <template v-else-if="node.type === 'cwe'">
        <div class="detail-section">
          <div class="section-title">Common Weakness Enumeration</div>
          <p class="section-text">This weakness category represents a class of vulnerabilities that can be exploited through specific attack patterns.</p>
        </div>

        <div v-if="connectedCves.length" class="detail-section">
          <div class="section-title">Related CVEs ({{ connectedCves.length }})</div>
          <div class="chips-container">
            <span v-for="cve in connectedCves.slice(0, 6)" :key="cve" class="detail-chip cve">{{ cve }}</span>
            <span v-if="connectedCves.length > 6" class="more-count">+{{ connectedCves.length - 6 }} more</span>
          </div>
        </div>

        <div v-if="connectedCapecs.length" class="detail-section">
          <div class="section-title">Attack Patterns ({{ connectedCapecs.length }})</div>
          <div class="chips-container">
            <span v-for="capec in connectedCapecs.slice(0, 8)" :key="capec" class="detail-chip capec">{{ capec }}</span>
            <span v-if="connectedCapecs.length > 8" class="more-count">+{{ connectedCapecs.length - 8 }} more</span>
          </div>
        </div>
      </template>

      <!-- CAPEC Details -->
      <template v-else-if="node.type === 'capec'">
        <div class="detail-section">
          <div class="section-title">Common Attack Pattern Enumeration</div>
          <p class="section-text">This attack pattern describes a method that adversaries use to exploit weaknesses in systems.</p>
        </div>

        <div v-if="connectedCwes.length" class="detail-section">
          <div class="section-title">Related Weaknesses ({{ connectedCwes.length }})</div>
          <div class="chips-container">
            <span v-for="cwe in connectedCwes.slice(0, 6)" :key="cwe" class="detail-chip cwe">{{ cwe }}</span>
            <span v-if="connectedCwes.length > 6" class="more-count">+{{ connectedCwes.length - 6 }} more</span>
          </div>
        </div>

        <div v-if="connectedTtps.length" class="detail-section">
          <div class="section-title">Related TTPs ({{ connectedTtps.length }})</div>
          <div class="chips-container">
            <span v-for="ttp in connectedTtps.slice(0, 8)" :key="ttp" class="detail-chip ttp">{{ ttp }}</span>
            <span v-if="connectedTtps.length > 8" class="more-count">+{{ connectedTtps.length - 8 }} more</span>
          </div>
        </div>
      </template>

      <!-- APT Group Details -->
      <template v-else-if="node.type === 'apt'">
        <div v-if="node.data?.operational_status" class="status-badge" :class="statusClass">
          {{ node.data.operational_status }}
        </div>

        <div v-if="node.data?.description" class="detail-section">
          <div class="section-title">Description</div>
          <p class="section-text">{{ truncateText(cleanDescription(node.data.description), 300) }}</p>
        </div>

        <div v-if="node.data?.aliases?.length" class="detail-section">
          <div class="section-title">Also Known As</div>
          <div class="chips-container">
            <span v-for="alias in node.data.aliases.slice(0, 5)" :key="alias" class="detail-chip alias">{{ alias }}</span>
          </div>
        </div>

        <div v-if="node.data?.targeting_sectors?.length" class="detail-section">
          <div class="section-title">Target Sectors</div>
          <div class="chips-container">
            <span v-for="sector in uniqueSectors.slice(0, 6)" :key="sector" class="detail-chip sector">{{ sector }}</span>
          </div>
        </div>

        <div v-if="node.data?.targeting_regions?.length" class="detail-section">
          <div class="section-title">Target Regions</div>
          <div class="chips-container">
            <span v-for="region in uniqueRegions.slice(0, 6)" :key="region" class="detail-chip region">{{ region }}</span>
          </div>
        </div>

        <div v-if="connectedTtps.length" class="detail-section">
          <div class="section-title">TTPs Used ({{ connectedTtps.length }})</div>
          <div class="chips-container">
            <span v-for="ttp in connectedTtps.slice(0, 8)" :key="ttp" class="detail-chip ttp">{{ ttp }}</span>
            <span v-if="connectedTtps.length > 8" class="more-count">+{{ connectedTtps.length - 8 }} more</span>
          </div>
        </div>

        <!-- APT Stats -->
        <div v-if="node.data?.total_techniques || node.data?.relevancy" class="stats-row small">
          <div v-if="node.data?.total_techniques" class="stat-item">
            <span class="stat-value">{{ node.data.total_techniques }}</span>
            <span class="stat-label">Total TTPs</span>
          </div>
          <div v-if="node.data?.relevancy" class="stat-item">
            <span class="stat-value">{{ Math.round(node.data.relevancy * 100) }}%</span>
            <span class="stat-label">Relevancy</span>
          </div>
        </div>
      </template>

      <!-- Summary Stats (for all types) -->
      <div class="stats-row summary" v-if="node.type !== 'org'">
        <div class="stat-item">
          <span class="stat-value">{{ directConnections }}</span>
          <span class="stat-label">Connections</span>
        </div>
      </div>
        </div>
      </div>

      <!-- Back Side - Reasoning Details (only for APT groups) -->
      <div class="flip-card-back" v-if="hasReasoning">
        <div class="details-content reasoning-content">
          <div class="reasoning-header">
            <v-icon small color="#00f5ff" class="mr-2">mdi-lightbulb-on-outline</v-icon>
            <span class="reasoning-title">Threat Assessment Reasoning</span>
          </div>

          <!-- Threat Level Score -->
          <div v-if="reasoningData.threat_level_score" class="detail-section">
            <div class="section-title">Threat Level Score</div>
            <div class="threat-score-bar">
              <div class="threat-score-fill" :style="{ width: `${reasoningData.threat_level_score * 100}%` }" />
              <span class="threat-score-value">{{ Math.round(reasoningData.threat_level_score * 100) }}%</span>
            </div>
          </div>

          <!-- TTP Overlap Stats -->
          <div class="detail-section">
            <div class="section-title">TTP Overlap Analysis</div>
            <div class="overlap-stats">
              <div v-if="reasoningData.ttp_basic_overlap" class="overlap-stat">
                <span class="overlap-label">Basic Overlap</span>
                <span class="overlap-value">{{ reasoningData.ttp_basic_overlap }}</span>
              </div>
              <div v-if="reasoningData.ttp_mature_overlap" class="overlap-stat">
                <span class="overlap-label">Mature Overlap</span>
                <span class="overlap-value">{{ reasoningData.ttp_mature_overlap }}</span>
              </div>
              <div v-if="reasoningData.ttp_recent_overlap" class="overlap-stat">
                <span class="overlap-label">Recent Overlap</span>
                <span class="overlap-value">{{ reasoningData.ttp_recent_overlap }}</span>
              </div>
              <div v-if="reasoningData.ttp_sophisticated_overlap" class="overlap-stat">
                <span class="overlap-label">Sophisticated Overlap</span>
                <span class="overlap-value">{{ reasoningData.ttp_sophisticated_overlap }}</span>
              </div>
            </div>
          </div>

          <!-- Reasoning Summary -->
          <div v-if="reasoningData.reasoning_summary" class="detail-section reasoning-summary-section">
            <div class="section-title">Analysis Summary</div>
            <div class="reasoning-summary-text" v-html="formatReasoningSummary(reasoningData.reasoning_summary)" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ThreatNodeDetails',
  props: {
    node: { type: Object, required: true },
    connections: { type: Array, default: () => [] },
    allNodes: { type: Array, default: () => [] },
    isDark: { type: Boolean, default: true },
  },
  data() {
    return {
      showReasoning: false,
    };
  },
  watch: {
    // Reset flip state when node changes
    node() {
      this.showReasoning = false;
    },
  },
  computed: {
    nodeTypeLabel() {
      const labels = {
        org: 'Organization', cve: 'CVE', cwe: 'CWE', capec: 'CAPEC', ttp: 'TTP', apt: 'APT Group',
      };
      return labels[this.node.type] || 'Node';
    },
    hasReasoning() {
      return this.node.type === 'apt' && this.node.data?.reasoning && Object.keys(this.node.data.reasoning).length > 0;
    },
    reasoningData() {
      return this.node.data?.reasoning || {};
    },
    cvssClass() {
      const score = parseFloat(this.node.data?.cvss || 0);
      if (score >= 9.0) return 'critical';
      if (score >= 7.0) return 'high';
      if (score >= 4.0) return 'medium';
      return 'low';
    },
    statusClass() {
      const status = (this.node.data?.operational_status || '').toLowerCase();
      if (status.includes('active')) return 'active';
      if (status.includes('inactive')) return 'inactive';
      return '';
    },
    connectedCves() { return this.getConnectedNodesByType('cve'); },
    connectedCwes() { return this.getConnectedNodesByType('cwe'); },
    connectedCapecs() { return this.getConnectedNodesByType('capec'); },
    connectedTtps() { return this.getConnectedNodesByType('ttp'); },
    connectedApts() { return this.getConnectedNodesByType('apt'); },
    directConnections() {
      return this.connections.filter(
        c => c.source === this.node.id || c.target === this.node.id,
      ).length;
    },
    relatedAptGroups() {
      // Find APT groups that use TTPs connected to this CVE
      if (this.node.type !== 'cve') return [];
      const cveTtps = new Set(this.connectedTtps);
      return this.allNodes
        .filter(n => n.type === 'apt')
        .filter(apt => {
          const aptTtps = this.getAptTtps(apt);
          return aptTtps.some(ttp => cveTtps.has(ttp));
        })
        .map(apt => ({ name: apt.label, color: apt.data?.color }))
        .slice(0, 5);
    },
    uniqueSectors() {
      const sectors = this.node.data?.targeting_sectors || [];
      return [...new Set(sectors.map(s => s.name || s))];
    },
    uniqueRegions() {
      const regions = this.node.data?.targeting_regions || [];
      return [...new Set(regions.map(r => r.name || r))];
    },
  },
  methods: {
    getConnectedNodesByType(type) {
      const connected = new Set();
      const nodeId = this.node.id;
      this.connections.forEach(conn => {
        if (conn.source === nodeId) {
          const targetNode = this.allNodes.find(n => n.id === conn.target);
          if (targetNode?.type === type) connected.add(targetNode.label);
        }
        if (conn.target === nodeId) {
          const sourceNode = this.allNodes.find(n => n.id === conn.source);
          if (sourceNode?.type === type) connected.add(sourceNode.label);
        }
      });
      return Array.from(connected);
    },
    getAptTtps(apt) {
      const ttps = apt.data?.TTPs || apt.data?.ttps || apt.data?.overlap_ttps || [];
      return ttps.map(t => {
        if (typeof t === 'string') {
          const match = t.match(/^(T\d+(?:\.\d+)?)/);
          return match ? match[1] : t;
        }
        return t.id || t;
      });
    },
    formatTactic(tactic) {
      return tactic.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    },
    truncateText(text, maxLength) {
      if (!text || text.length <= maxLength) return text;
      return `${text.substring(0, maxLength)}...`;
    },
    cleanDescription(desc) {
      if (!desc) return '';
      // Remove markdown links like [text](url)
      return desc.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    },
    toggleReasoning() {
      this.showReasoning = !this.showReasoning;
    },
    formatReasoningSummary(summary) {
      if (!summary) return '';
      // Convert markdown-style formatting to HTML
      let formatted = summary
        // Convert headers (# Title)
        .replace(/^# (.+)$/gm, '<h4 class="reasoning-heading">$1</h4>')
        // Convert bold (**text**)
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        // Convert line breaks
        .replace(/\n\n/g, '</p><p>')
        .replace(/\n/g, '<br>');
      // Wrap in paragraph if not already
      if (!formatted.startsWith('<')) {
        formatted = `<p>${formatted}</p>`;
      }
      return formatted;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.threat-node-details {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--graph-panel-bg);
  color: var(--graph-text);
  transition: background $transition-duration-normal ease, color $transition-duration-normal ease;

  &.light-theme {
    border-left: $border-width-thin solid var(--graph-panel-border);
  }
}

.details-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: $space-4;
  border-bottom: $border-width-thin solid var(--graph-panel-border);
}

.node-type-badge {
  font-size: $font-size-xs;
  font-weight: 600;
  text-transform: uppercase;
  padding: $space-1 $space-3;
  border-radius: $radius-xl;
  letter-spacing: 0.5px;

  &.org { background: var(--node-org-bg); color: var(--node-org); }
  &.cve { background: var(--node-cve-bg); color: var(--node-cve); }
  &.cwe { background: var(--node-cwe-bg); color: var(--node-cwe); }
  &.capec { background: var(--node-capec-bg); color: var(--node-capec); }
  &.ttp { background: var(--node-ttp-bg); color: var(--node-ttp); }
  &.apt { background: var(--node-apt-bg); color: var(--node-apt); }
}

.details-content {
  flex: 1;
  overflow-y: auto;
  padding: $space-4;
}

.node-title {
  font-size: $font-size-md;
  font-weight: 600;
  margin-bottom: $space-4;
  font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
  word-break: break-word;
  color: var(--graph-text);
}

.cvss-badge {
  display: inline-block;
  font-size: $font-size-sm;
  font-weight: 600;
  padding: $space-1 $space-3;
  border-radius: $radius-md;
  margin-bottom: $space-4;

  &.critical { background: $cvss-critical-bg; color: $cvss-critical-text; }
  &.high { background: $cvss-high-bg; color: $cvss-high-text; }
  &.medium { background: $cvss-medium-bg; color: $cvss-medium-text; }
  &.low { background: $cvss-low-bg; color: $cvss-low-text; }
}

.status-badge {
  display: inline-block;
  font-size: $font-size-xs;
  font-weight: 500;
  padding: $space-1 $space-3;
  border-radius: $radius-xl;
  margin-bottom: $space-4;
  background: var(--status-inactive-bg);
  color: var(--status-inactive-text);

  &.active { background: var(--status-active-bg); color: var(--status-active-text); }
  &.inactive { background: var(--status-inactive-bg); color: var(--status-inactive-text); }
}

.maturity-badge {
  display: inline-block;
  font-size: $font-size-xs;
  padding: $space-1 $space-2;
  border-radius: $radius-md;
  background: var(--chip-ttp-bg);
  color: var(--chip-ttp-text);
}

.detail-section {
  margin-bottom: $space-5;
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: $space-2;
}

.section-icon {
  margin-right: $space-1;
  color: var(--reasoning-accent);
}

.section-title {
  font-size: $font-size-xs;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: $space-2;
}

.section-header .section-title {
  margin-bottom: 0;
}

.section-text {
  font-size: $font-size-base - 0.0625rem;
  line-height: $line-height-relaxed;
  color: var(--text-primary);
  margin: 0;

  &.highlight-blue {
    padding: $space-2 $space-3;
    background: var(--highlight-blue-bg);
    border-left: 3px solid var(--highlight-blue-border);
    border-radius: $radius-md;
  }

  &.highlight-yellow {
    padding: $space-2 $space-3;
    background: var(--highlight-yellow-bg);
    border-left: 3px solid var(--highlight-yellow-border);
    border-radius: $radius-md;
  }

  &.truncate-text {
    @include line-clamp(2);
    cursor: pointer;
  }
}

.chips-container {
  display: flex;
  flex-wrap: wrap;
  gap: $space-1;
  align-items: center;
}

.detail-chip {
  font-size: $font-size-xs;
  padding: $space-1 $space-2;
  border-radius: $radius-xl;
  font-weight: 500;

  &.cve { background: var(--chip-cve-bg); color: var(--chip-cve-text); }
  &.cwe { background: var(--chip-cwe-bg); color: var(--chip-cwe-text); }
  &.capec { background: var(--chip-capec-bg); color: var(--chip-capec-text); }
  &.ttp { background: var(--chip-ttp-bg); color: var(--chip-ttp-text); }
  &.apt { background: var(--chip-apt-bg); color: var(--chip-apt-text); }
  &.tactic { background: var(--chip-tactic-bg); color: var(--chip-tactic-text); }
  &.sector, &.region { background: var(--chip-sector-bg); color: var(--chip-sector-text); }
  &.alias { background: var(--chip-alias-bg); color: var(--chip-alias-text); font-style: italic; }
}

.more-count {
  font-size: $font-size-xs;
  color: var(--more-count-text);
  font-style: italic;
}

.apt-list {
  display: flex;
  flex-direction: column;
  gap: $space-2;
}

.apt-item-small {
  display: flex;
  align-items: center;
  gap: $space-2;
  padding: $space-1 $space-2;
  background: var(--chip-apt-bg);
  border-radius: $radius-lg;
}

.apt-color-dot {
  width: $space-2;
  height: $space-2;
  border-radius: $radius-full;
  flex-shrink: 0;
}

.apt-name-text {
  font-size: $font-size-sm;
  color: var(--text-primary);
}

.stats-row {
  display: flex;
  justify-content: space-around;
  padding: $space-4;
  background: var(--stats-gradient);
  border-radius: $radius-lg;
  margin-top: $space-4;

  &.small {
    padding: $space-3;
    background: var(--stats-small-bg);
  }

  &.summary {
    margin-top: auto;
    background: var(--stats-summary-bg);
  }
}

.stat-item {
  text-align: center;
}

.stat-value {
  display: block;
  font-size: $font-size-2xl;
  font-weight: 700;
  color: var(--graph-text);
}

.stat-label {
  font-size: $font-size-xs;
  color: var(--text-secondary);
  text-transform: uppercase;
}

.small .stat-value { font-size: $font-size-lg; }
.small .stat-label { font-size: $font-size-2xs; }

// Header actions container
.header-actions {
  display: flex;
  align-items: center;
  gap: $space-1;
}

// Reasoning toggle button
.reasoning-toggle-btn {
  transition: all $transition-duration-normal ease;

  &.active {
    background: var(--highlight-blue-bg);
  }
}

// Flip card container
.flip-card-container {
  flex: 1;
  position: relative;
  perspective: 1000px;
  overflow: hidden;
}

.flip-card-front,
.flip-card-back {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  transition: transform 0.6s ease;
  overflow-y: auto;
}

.flip-card-front {
  transform: rotateY(0deg);
}

.flip-card-back {
  transform: rotateY(180deg);
}

.flip-card-container.flipped {
  .flip-card-front {
    transform: rotateY(-180deg);
  }

  .flip-card-back {
    transform: rotateY(0deg);
  }
}

// Reasoning content styles
.reasoning-content {
  background: var(--reasoning-gradient);
}

.reasoning-header {
  display: flex;
  align-items: center;
  margin-bottom: $space-5;
  padding-bottom: $space-3;
  border-bottom: $border-width-thin solid var(--reasoning-border);
}

.reasoning-title {
  font-size: $font-size-base;
  font-weight: 600;
  color: var(--reasoning-accent);
}

// Threat score bar
.threat-score-bar {
  position: relative;
  height: $space-6;
  background: var(--stats-small-bg);
  border-radius: $radius-xl;
  overflow: hidden;
}

.threat-score-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: linear-gradient(90deg, var(--node-org) 0%, var(--node-apt) 100%);
  border-radius: $radius-xl;
  transition: width 0.5s ease;
}

.threat-score-value {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: $font-size-sm;
  font-weight: 600;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

// Overlap stats
.overlap-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: $space-2;
}

.overlap-stat {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: $space-2 $space-3;
  background: var(--stats-small-bg);
  border-radius: $radius-lg;
}

.overlap-label {
  font-size: $font-size-xs;
  color: var(--text-secondary);
}

.overlap-value {
  font-size: $font-size-base;
  font-weight: 600;
  color: var(--reasoning-accent);
}

// Reasoning summary
.reasoning-summary-section {
  margin-top: $space-4;
}

.reasoning-summary-text {
  font-size: $font-size-base - 0.0625rem;
  line-height: 1.7;
  color: var(--text-primary);

  :deep(p) {
    margin-bottom: $space-3;

    &:last-child {
      margin-bottom: 0;
    }
  }

  :deep(strong) {
    color: var(--reasoning-accent);
  }

  :deep(.reasoning-heading) {
    font-size: $font-size-base;
    font-weight: 600;
    color: var(--graph-text);
    margin: $space-4 0 $space-2 0;

    &:first-child {
      margin-top: 0;
    }
  }
}
</style>

<style lang="scss">
@import '~@/assets/styles/design-system';

// Global tooltip styles (not scoped)
.detail-tooltip.detail-tooltip {
  background: var(--tooltip-bg);
  color: var(--tooltip-text);
  font-size: $font-size-sm;
  line-height: $line-height-normal;
  padding: $space-3 $space-4;
  border-radius: $radius-lg;
  border: $border-width-thin solid var(--reasoning-border);
  box-shadow: $shadow-lg;
}
</style>
